import { WagmiProvider, type Config, createConfig, http } from 'wagmi';
import { arbitrum, mainnet } from 'wagmi/chains';
import { injected, coinbaseWallet, walletConnect } from 'wagmi/connectors';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useWalletConfig } from '@/hooks/useWalletConfig';

// Create query client
const queryClient = new QueryClient();

// Define chains
const chains = [arbitrum, mainnet] as const;

// Metadata - url must match origin for WalletConnect to work
const metadata = {
  name: 'sharpexio',
  description: 'Hyperliquid Analytics Dashboard',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://app.sharpex.io',
  icons: ['https://app.sharpex.io/favicon.ico'],
};

interface Web3ProviderProps {
  children: ReactNode;
}

export function Web3Provider({ children }: Web3ProviderProps) {
  const [mounted, setMounted] = useState(false);
  const walletConfig = useWalletConfig();

  const resolvedProjectId =
    walletConfig.loading || !walletConfig.configured ? undefined : walletConfig.walletConnectProjectId;

  const wagmiConfig = useMemo(() => {
    const connectors = [
      injected(),
      coinbaseWallet({ appName: metadata.name }),
      ...(resolvedProjectId
        ? [
            walletConnect({
              projectId: resolvedProjectId,
              metadata,
              showQrModal: true,
            }),
          ]
        : []),
    ];

    return createConfig({
      chains,
      connectors,
      transports: {
        [mainnet.id]: http(),
        [arbitrum.id]: http(),
      },
    });
  }, [resolvedProjectId]);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!walletConfig.loading && resolvedProjectId) {
      console.log('WalletConnect project ID resolved:', resolvedProjectId);
    } else if (!walletConfig.loading && !walletConfig.configured) {
      console.warn('WalletConnect not configured - WalletConnect will be unavailable');
    }
  }, [walletConfig.loading, walletConfig.configured, resolvedProjectId]);

  // Prevent hydration mismatch
  if (!mounted || walletConfig.loading) {
    return null;
  }

  return (
    <WagmiProvider config={wagmiConfig as Config}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </WagmiProvider>
  );
}
